import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Group } from 'src/app/shared/models/interfaces';
import { GroupsService } from '../groups.service';
import { ActivatedRoute } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule, MAT_DATE_FORMATS, DateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { ReactiveFormsModule } from '@angular/forms';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import { Router } from '@angular/router';
import { UIComponentsModule } from 'src/app/shared/ui-components/ui-components.module';
import { DATE_FORMATS } from 'src/app/shared/constants/date-formats';

@Component({
  selector: 'app-group-mask',
  imports: [
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    ReactiveFormsModule,
    MatIconModule,
    UIComponentsModule,
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS }
  ],
  templateUrl: './group-mask.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './group-mask.component.scss',
})
export class GroupMaskComponent {
  groupFormGroup!: FormGroup;
  mode: 'create' | 'edit' = 'create';
  error: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private groupsService: GroupsService,
    private snackbar: SnackbarService,
    private router: Router
  ) {}
  ngOnInit(): void {
    
    // Initialize the form with default values
    this.groupFormGroup = this.formBuilder.group({
      id: [''],
      groupName: ['', [Validators.required]],
      trackingPeriodStart: ['', [Validators.required]],
      trackingPeriodFinish: ['', [Validators.required]],
    });

    this.route.queryParams.subscribe((params) => {
      // Editing an already existing group
      if (params['mode'] === 'edit') {
        this.mode = 'edit';
        const group = this.groupsService.getSelectedGroup();
        if (group) {
          this.groupFormGroup.patchValue({
            id: group.id,
            groupName: group.groupName,
            trackingPeriodStart: group.trackingPeriodStart,
            trackingPeriodFinish: group.trackingPeriodFinish,
          });
        } else {
          console.error('Group not found');
        }

        // Creating a new group
      } else {
        this.mode = 'create';
        this.groupFormGroup.patchValue({
          id: '',
          groupName: '',
          trackingPeriodStart: new Date(),
          trackingPeriodFinish: new Date(),
        });
      }
    });
  }

  onsubmit(): void {
    if (this.groupFormGroup.invalid) {
      console.error('Form is invalid');
      return;
    }

    if (this.mode === 'edit') {
      //  Save changes of already existing group
      this.groupsService.updateGroup(this.groupFormGroup.value).subscribe({
        next: (group: Group) => {
          this.snackbar.showSuccessMessage('Group updated successfully');
          // navigate to group details page
          this.groupsService.setSelectedGroup(group);
          this.groupFormGroup.reset();
          this.router.navigate(['/group', group.id]);
        },
        error: (error) => {
          if (error.status === 403) {
            this.snackbar.showErrorMessage(
              'You do not have permission to update this group. You may not be an admin of this group.'
            );
          } else {
            this.snackbar.showErrorMessage('Failed to update group');
          }
          console.error('Error updating group:', error);
        },
      });
    } else {
      // Create a new group
      this.groupsService.createGroup(this.groupFormGroup.value).subscribe({
        next: (group: Group) => {
          this.snackbar.showSuccessMessage('Group created successfully');
          // navigate to group details page
          this.groupsService.setSelectedGroup(group);
          this.groupFormGroup.reset();
          this.router.navigate(['/group', group.id]);
        },
        error: (error) => {
          if (error.status === 409) {
            this.snackbar.showErrorMessage(
              'A group with this name already exists. Please choose a different name.'
            );
          } else if (error.status === 403) {
            this.snackbar.showErrorMessage('You do not have permission to create groups.');
          } else {
            this.snackbar.showErrorMessage('Failed to create group');
          }
          console.error('Error creating group:', error);
        },
      });
    }
  }
}
